import { useCallback, useEffect, useRef, useState } from 'react'

import CallQualityIntentForm, {
  type CallQualityWizardIntentFormSubmissionSchema,
} from './CallQualityWizard/CallQualityWizardIntentForm'
import CallQualityWizardIntentStep from './CallQualityWizard/CallQualityWizardIntentStep'
import CallQualityWizardIssueForm from './CallQualityWizard/CallQualityWizardIssueForm'
import CallQualityWizardIssueStep from './CallQualityWizard/CallQualityWizardIssueStep'
import CallQualityWizardTroubleshootStep from './CallQualityWizard/CallQualityWizardTroubleshootStep'
import type {
  CallQualityWizardIssueFormIssueType,
  CallQualityWizardIssueFormSubmissionSchema,
} from './CallQualityWizard/callQualityWizardIssueFormCommon'
import type { EffectiveConnectionType } from './NetworkInformationUiStore'

type WizardState = 'close' | 'intent' | 'negative' | 'negative-troubleshoot'

type Submission =
  | CallQualityWizardIntentFormSubmissionSchema
  | CallQualityWizardIssueFormSubmissionSchema

type AudioInterfaces = 'airplay' | 'bluetooth' | 'headset' | 'system' | 'unknown'

export type FeedbackEvent = 'call_quality_rated' | 'call_quality_reason_given'

type Feedback = {
  platform?: 'web' | 'desktop'
  subPlatform?: NodeJS.Platform
  feedback: -1 | 0 | 1
  feedbackType: 'call'
  /**
   * Schema version, not to be confused with the version of the app.
   */
  version: 1
  appVersion: string
  userText?: string
  callQuality: {
    network: {
      interface: string
      effectiveConnectionType: EffectiveConnectionType
    }[]
    callId: string
    issues: CallQualityWizardIssueFormIssueType[]
    duration: number
    warningsDuration: number
    networkInterruption: boolean
    audioInterface: AudioInterfaces
    bitrateType?: 'fixed' | 'variable'
    bandwidthEstimate?: number
    jitter?: number
    packetLossPercentage?: number
    rtt?: number
    mos?: number
  }
}

interface CallQualityWizardProps {
  audioInterface: string
  callId: string
  callDuration: number
  initialState?: WizardState
  onComplete?: (state: WizardState, feedback: Feedback) => void
  onEvent?: (eventName: FeedbackEvent, feedback: Feedback) => void
  onMount?: () => void
  networkInformation: {
    connectionType: string
    effectiveConnectionType: EffectiveConnectionType
  }
  finalCallMetrics?: {
    platform: 'web' | 'desktop'
    subPlatform?: NodeJS.Platform
    bitrateType: 'fixed' | 'variable'
    bandwidthEstimate?: number
    jitter?: number
    packetsLostPercentage?: number
    rtt?: number
    mos?: number
  } | null
  appVersion: string
}

function CallQualityWizard({
  audioInterface,
  appVersion,
  callId,
  callDuration,
  initialState = 'intent',
  onComplete,
  onEvent,
  onMount,
  finalCallMetrics,
  networkInformation,
}: CallQualityWizardProps) {
  const [state, setState] = useState<WizardState>(initialState)
  const timeoutRef = useRef<number | null>(null)
  // TODO: COM-9761
  const [feedback, setFeedback] = useState<Feedback>({
    platform: finalCallMetrics?.platform ?? 'web',
    subPlatform: finalCallMetrics?.subPlatform,
    feedbackType: 'call',
    feedback: -1,
    version: 1,
    userText: '',
    appVersion,
    callQuality: {
      callId,
      issues: [],
      duration: callDuration,
      warningsDuration: 0,
      networkInterruption: false,
      bandwidthEstimate: finalCallMetrics?.bandwidthEstimate,
      bitrateType: finalCallMetrics?.bitrateType ?? 'variable',
      jitter: finalCallMetrics?.jitter,
      packetLossPercentage: finalCallMetrics?.packetsLostPercentage,
      rtt: finalCallMetrics?.rtt,
      mos: finalCallMetrics?.mos,
      network: [
        {
          interface: networkInformation.connectionType,
          effectiveConnectionType: networkInformation.effectiveConnectionType,
        },
      ],
      audioInterface: audioInterface as AudioInterfaces,
    },
  })
  const handleSubmit = useCallback(
    async (submission: Submission) => {
      if (submission.type === 'intent') {
        const updatedFeedback = {
          ...feedback,
          feedback: submission.payload.intent === 'positive' ? 1 : 0,
        } satisfies Feedback
        onEvent?.('call_quality_rated', updatedFeedback)
        if (submission.payload.intent === 'positive') {
          return onComplete?.(state, updatedFeedback)
        }
        setFeedback(updatedFeedback)
        setState('negative')
      } else if (submission.type === 'issue') {
        const updatedFeedback = {
          ...feedback,
          callQuality: {
            ...feedback.callQuality,
            issues: [submission.payload.issue],
          },
          userText: submission.payload.issueText,
        } satisfies Feedback
        onEvent?.('call_quality_reason_given', updatedFeedback)
        onComplete?.(state, updatedFeedback)
        setFeedback(updatedFeedback)
        setState('negative-troubleshoot')
      }
    },
    [state, feedback, onComplete, onEvent],
  )
  const handleClose = useCallback(() => {
    onComplete?.('close', feedback)
  }, [onComplete, feedback])
  useEffect(() => {
    const timeoutHandle = timeoutRef.current
    // clear the timeout if we are on negative
    // when we are in the negative state, there is an intent by the user
    // to submit their negative feedback, so we don't want to auto-submit.
    // if we are in the troubleshoot state, then we want to keep the timeout
    // so that it will auto-dismiss after 30 seconds if they dont immediately close it.
    if (state === 'negative' && timeoutHandle !== null) {
      clearTimeout(timeoutHandle)
      timeoutRef.current = null
      return undefined
    }
    timeoutRef.current = window.setTimeout(() => {
      onComplete?.('close', feedback)
    }, 30_000) // 30 seconds
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [state, feedback, onComplete])
  useEffect(() => {
    onMount?.()
    // eslint-disable-next-line react-compiler/react-compiler -- UXP-3732 - Fix React Compiler errors
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const handleIssueChange = useCallback(
    (issue: CallQualityWizardIssueFormIssueType) => {
      setFeedback({
        ...feedback,
        callQuality: {
          ...feedback.callQuality,
          issues: [issue],
        },
      })
    },
    [feedback],
  )
  const [issue] = feedback.callQuality.issues
  if (state === 'intent') {
    return (
      <CallQualityWizardIntentStep onClose={handleClose}>
        <CallQualityIntentForm onSubmit={handleSubmit} />
      </CallQualityWizardIntentStep>
    )
  } else if (state === 'negative') {
    return (
      <CallQualityWizardIssueStep onClose={handleClose}>
        <CallQualityWizardIssueForm
          onSubmit={handleSubmit}
          onIssueChange={handleIssueChange}
        />
      </CallQualityWizardIssueStep>
    )
  } else if (
    state === 'negative-troubleshoot' &&
    issue !== 'other' &&
    issue !== undefined
  ) {
    return <CallQualityWizardTroubleshootStep issue={issue} onClose={handleClose} />
  }
  return null
}

export type {
  Feedback as CallQualityWizardFeedback,
  WizardState as CallQualityWizardState,
}

export default CallQualityWizard
